import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Row, Col, Form } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate, useParams } from "react-router-dom";

export default function SalaryExpenseForm() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const API_BASE = `${process.env.REACT_APP_API_URL}/admin/salary-expenses`;
    const EMPLOYEES_API = `${process.env.REACT_APP_API_URL}/admin/emplyees`;
    const BANKS_API = `${process.env.REACT_APP_API_URL}/admin/banks`;
    const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } };

    const [employees, setEmployees] = useState([]);
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    const [formData, setFormData] = useState({
        employee_id: "",
        salary_month: "",
        date: "",
        salary: "",
        bank_id: "",
        amount: "",
        payment_date: "",
        payment_notes: ""
    });
    const [errors, setErrors] = useState({});

    const fetchDependencies = useCallback(async () => {
        try {
            const [empRes, bankRes] = await Promise.all([
                axios.get(EMPLOYEES_API, authHeaders),
                axios.get(BANKS_API, authHeaders)
            ]);
            setEmployees(empRes.data?.data || empRes.data || []);
            setBanks(bankRes.data?.data || bankRes.data || []);
        } catch (err) {
            console.error(err);
        }
    }, [EMPLOYEES_API, BANKS_API]);

    const fetchExpenseDetails = useCallback(async () => {
        if (!isEdit) return;
        try {
            const res = await axios.get(`${API_BASE}/${id}`, authHeaders);
            const data = res.data?.data || res.data;

            setFormData({
                employee_id: data.employee?.id || data.employee_id || "",
                salary_month: data.salary_month || "",
                date: data.date || "",
                salary: data.salary || "",
                bank_id: data.payment?.bank?.id || data.payment?.bank_id || "",
                amount: data.payment?.amount || "",
                payment_date: data.payment?.payment_date || "",
                payment_notes: data.payment?.notes || ""
            });
        } catch (err) {
            console.error(err);
        }
    }, [API_BASE, id, isEdit]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await fetchDependencies();
            if (isEdit) {
                await fetchExpenseDetails();
            }
            setLoading(false);
        };
        init();
    }, [fetchDependencies, fetchExpenseDetails, isEdit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        setErrors({});

        try {
            let res;
            if (isEdit) {
                res = await axios.put(`${API_BASE}/${id}`, formData, authHeaders);
            } else {
                res = await axios.post(API_BASE, formData, authHeaders);
            }

            if (res.data?.success || res.status === 200 || res.status === 201) {
                navigate("/salary-expenses");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                console.error("Submit Error:", err);
            }
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return <PageLayout><p>{t("loading")}...</p></PageLayout>;
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{isEdit ? t("edit salary expense") : t("add salary expense")}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="/salary-expenses" className="mc-breadcrumb-link">{t("salary expenses")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{isEdit ? t("edit") : t("create")}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header p-3">
                            <h5 className="mc-card-title mb-0">{isEdit ? t("edit salary expense details") : t("add salary expense details")}</h5>
                        </div>
                        <Form onSubmit={handleSubmit} className="p-3">
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("employee")} <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            name="employee_id"
                                            value={formData.employee_id}
                                            onChange={handleChange}
                                            isInvalid={!!errors.employee_id}
                                            required
                                        >
                                            <option value="">{t("select employee")}</option>
                                            {employees.map((emp) => (
                                                <option key={emp.id} value={emp.id}>
                                                    {emp.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.employee_id?.[0]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("salary month")} <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="month"
                                            name="salary_month"
                                            value={formData.salary_month}
                                            onChange={handleChange}
                                            isInvalid={!!errors.salary_month}
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.salary_month?.[0]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("expense date")} <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            isInvalid={!!errors.date}
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.date?.[0]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("salary amount")} <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="any"
                                            name="salary"
                                            value={formData.salary}
                                            onChange={handleChange}
                                            isInvalid={!!errors.salary}
                                            placeholder="Enter salary amount"
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.salary?.[0]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("bank")} <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            name="bank_id"
                                            value={formData.bank_id}
                                            onChange={handleChange}
                                            isInvalid={!!errors.bank_id}
                                            required
                                        >
                                            <option value="">{t("select bank")}</option>
                                            {banks.map((bank) => (
                                                <option key={bank.id} value={bank.id}>
                                                    {bank.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.bank_id?.[0]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("payment amount")} <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="any"
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            isInvalid={!!errors.amount}
                                            placeholder="Enter payment amount"
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.amount?.[0]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("payment date")} <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="payment_date"
                                            value={formData.payment_date}
                                            onChange={handleChange}
                                            isInvalid={!!errors.payment_date}
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.payment_date?.[0]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("payment notes")}</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={1}
                                            name="payment_notes"
                                            value={formData.payment_notes}
                                            onChange={handleChange}
                                            isInvalid={!!errors.payment_notes}
                                            placeholder="Enter payment notes"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.payment_notes?.[0]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={12} className="d-flex justify-content-end gap-2 mt-3">
                                    <ButtonComponent
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => navigate("/salary-expenses")}
                                    >
                                        {t("cancel")}
                                    </ButtonComponent>
                                    <ButtonComponent
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={actionLoading}
                                    >
                                        {actionLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                                        {isEdit ? t("update") : t("save")}
                                    </ButtonComponent>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}